import React, { useState, useEffect, useContext, useMemo } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import * as XLSX from "xlsx";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { UsersFilter } from "../../components/UsersFilter";
import { TagsFilter } from "../../components/TagsFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import { ChannelFilter } from "../../components/ChannelFilter";
import { SupportTimeFilter } from "../../components/SupportTimeFilter";
import useDashboard from "../../hooks/useDashboard";
import QueueSelectCustom from "../../components/QueueSelectCustom";
import moment from "moment";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import { blue, green } from "@material-ui/core/colors";
import {
  Facebook,
  Forward,
  History,
  Instagram,
  SaveAlt,
  Visibility,
  WhatsApp,
} from "@material-ui/icons";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { Field } from "formik";

const parseSupportTimeRange = (value) => {
  if (!value) return { min: null, max: null };
  if (value === "60+") return { min: 60, max: null };
  const [minRaw, maxRaw] = String(value).split("-");
  const min = Number(minRaw);
  const max = Number(maxRaw);
  return {
    min: Number.isNaN(min) ? null : min,
    max: Number.isNaN(max) ? null : max,
  };
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.fancyBackground,
  },
  formControl: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  mainPaper: {
    flex: 1,
    marginTop: 40,
    borderRadius: 20,
    border: "0px !important",
    marginBottom: 40,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  mainPaperTable: {
    flex: 1,
    overflow: "auto",
    height: "68vh",
    ...theme.scrollbarStylesSoftBig,
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      maxHeight: "50vh",
      marginTop: theme.spacing(2),
    },
  },
  mainPaperFilter: {
    flex: 1,
    overflow: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStylesSoftBig,
    [theme.breakpoints.down("sm")]: {
      maxHeight: "20vh",
      overflowY: "auto",
      marginBottom: theme.spacing(2),
    },
  },
  mainHeaderBlock: {
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexWrap: "wrap",
    },
  },
  mainHeaderFilter: {
    [theme.breakpoints.down("sm")]: {
      position: "relative",
      zIndex: 1,
    },
  },
  filterItem: {
    width: 200,
    [theme.breakpoints.down("md")]: {
      width: "45%",
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  reportColumnsToolbarBlock: {
    paddingTop: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  reportColumnsSelect: {
    minWidth: 220,
    maxWidth: 320,
  },
  closingSummaryCardsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  closingSummaryCard: {
    minWidth: 140,
    maxWidth: 220,
    flex: "1 1 140px",
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  closingSummaryCardCount: {
    fontWeight: 700,
    fontSize: "1.75rem",
    lineHeight: 1.1,
    color: theme.palette.primary.main,
  },
  closingSummaryCardMeta: {
    marginTop: theme.spacing(0.5),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    wordBreak: "break-word",
  },
  tableStickyId: {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "2px 0 6px rgba(0,0,0,0.06)",
    whiteSpace: "nowrap",
  },
  tableStickyActions: {
    position: "sticky",
    right: 0,
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    boxShadow: "-2px 0 6px rgba(0,0,0,0.06)",
    whiteSpace: "nowrap",
  },
  tableHeadStickyRow: {
    "& th": {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

/** Colunas opcionais (entre ID fixo e Ações fixo), na ordem de exibição */
const REPORT_OPTIONAL_COLUMN_ORDER = [
  "whatsapp",
  "contact",
  "contactNumber",
  "user",
  "queue",
  "wallet",
  "kanban",
  "status",
  "dateOpen",
  "dateClose",
  "supportTime",
  "nps",
  "valorVenda",
  "motivoNaoVenda",
  "motivoFinalizacao",
  "finalizadoComVenda",
  "situacao",
  "canal",
  "regiao",
  "campanha",
  "produto",
];

/** Por padrão: Usuário, Status, Data inicial, Nome do cliente, Situação, Kanban */
const REPORT_DEFAULT_VISIBLE_OPTIONAL = [
  "user",
  "status",
  "dateOpen",
  "contact",
  "situacao",
  "kanban",
];

const reportTableColumnsStorageKey = (userId) =>
  userId != null && userId !== ""
    ? `i9:reportsTicketTableColumns:v1:${userId}`
    : null;

const getDefaultOptionalColumnVisibility = () => {
  const v = {};
  REPORT_OPTIONAL_COLUMN_ORDER.forEach((id) => {
    v[id] = REPORT_DEFAULT_VISIBLE_OPTIONAL.includes(id);
  });
  return v;
};

const loadOptionalColumnVisibility = (userId) => {
  const defaults = getDefaultOptionalColumnVisibility();
  const key = reportTableColumnsStorageKey(userId);
  if (!key) return defaults;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    const merged = { ...defaults };
    REPORT_OPTIONAL_COLUMN_ORDER.forEach((id) => {
      if (typeof parsed[id] === "boolean") merged[id] = parsed[id];
    });
    return merged;
  } catch (_e) {
    return defaults;
  }
};

const saveOptionalColumnVisibility = (userId, visibility) => {
  const key = reportTableColumnsStorageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(visibility));
  } catch (_e) {
    // quota / modo privado
  }
};

const closingSummaryPrefsKey = (userId) =>
  userId != null && userId !== ""
    ? `i9:reportsClosingSummaryPrefs:v1:${userId}`
    : null;

const defaultClosingSummaryPrefs = { visible: true, layout: "table" };

const loadClosingSummaryPrefs = (userId) => {
  const key = closingSummaryPrefsKey(userId);
  if (!key) return { ...defaultClosingSummaryPrefs };
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { ...defaultClosingSummaryPrefs };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...defaultClosingSummaryPrefs };
    const visible =
      typeof parsed.visible === "boolean"
        ? parsed.visible
        : defaultClosingSummaryPrefs.visible;
    const layout =
      parsed.layout === "cards" ? "cards" : defaultClosingSummaryPrefs.layout;
    return { visible, layout };
  } catch (_e) {
    return { ...defaultClosingSummaryPrefs };
  }
};

const saveClosingSummaryPrefs = (userId, prefs) => {
  const key = closingSummaryPrefsKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch (_e) {
    // ignore
  }
};

const optionalColumnAlign = (id) =>
  ["whatsapp", "contact", "contactNumber", "user", "queue", "wallet", "kanban"].includes(
    id
  )
    ? "left"
    : "center";

const reportOptionalColumnLabel = (id) => {
  const k = `reports.columnPicker.columns.${id}`;
  const t = i18n.t(k);
  return t !== k ? t : id;
};

const ticketKanbanLabel = (ticket) =>
  ticket?.kanbanLaneName || ticket?.kanban || "Em aberto";

const renderOptionalTicketCellValue = (ticket, colId) => {
  switch (colId) {
    case "whatsapp":
      return ticket?.whatsappName;
    case "contact":
      return ticket?.contactName;
    case "contactNumber":
      return ticket?.contactNumber || "-";
    case "user":
      return ticket?.userName;
    case "queue":
      return ticket?.queueName;
    case "wallet":
      return ticket?.walletName || "-";
    case "kanban":
      return ticketKanbanLabel(ticket);
    case "status":
      return ticket?.status;
    case "dateOpen":
      return ticket?.createdAt;
    case "dateClose":
      return ticket?.closedAt;
    case "supportTime":
      return ticket?.supportTime;
    case "nps":
      return ticket?.NPS;
    case "valorVenda":
      return ticket?.valorVenda !== undefined && ticket?.valorVenda !== null
        ? ticket.valorVenda
        : "-";
    case "motivoNaoVenda":
      return ticket?.motivoNaoVenda || "-";
    case "motivoFinalizacao":
      return ticket?.motivoFinalizacao || "-";
    case "finalizadoComVenda":
      return ticket?.finalizadoComVenda === true
        ? "Sim"
        : ticket?.finalizadoComVenda === false
        ? "Não"
        : "-";
    case "situacao":
      return ticket?.situacao || "-";
    case "canal":
      return ticket?.canal || "-";
    case "regiao":
      return ticket?.regiao || "-";
    case "campanha":
      return ticket?.campanha || "-";
    case "produto":
      return ticket?.produto || "-";
    default:
      return null;
  }
};

const Reports = () => {
  const classes = useStyles();
  const history = useHistory();
  const { getReport } = useDashboard();

  const { user } = useContext(AuthContext);

  const [optionalColumnVisible, setOptionalColumnVisible] = useState(() =>
    getDefaultOptionalColumnVisibility()
  );

  const selectedOptionalColumnIds = useMemo(
    () => REPORT_OPTIONAL_COLUMN_ORDER.filter((id) => optionalColumnVisible[id]),
    [optionalColumnVisible]
  );

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSizeSelect, setPageSizeSelect] = useState("10");
  const [customPageSize, setCustomPageSize] = useState("25");
  const [showFilters, setShowFilters] = useState(true);

  const resolvedPageSize = useMemo(() => {
    if (pageSizeSelect === "custom") {
      const n = parseInt(String(customPageSize).replace(/\D/g, ""), 10);
      if (!n || n < 1) return 10;
      return Math.min(999, n);
    }
    return parseInt(pageSizeSelect, 10) || 10;
  }, [pageSizeSelect, customPageSize]);

  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  const [tagIds, setTagIds] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(
    moment("1", "D").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);

  const [kanbanLanes, setKanbanLanes] = useState([]);
  const [selectedKanban, setSelectedKanban] = useState("");

  const [situacaoOptions, setSituacaoOptions] = useState([]);
  const [canalOptions, setCanalOptions] = useState([]);
  const [regiaoOptions, setRegiaoOptions] = useState([]);
  const [loadingCustomAttributes, setLoadingCustomAttributes] = useState(false);

  const [selectedSituacao, setSelectedSituacao] = useState("");
  const [selectedCanal, setSelectedCanal] = useState("");
  const [selectedRegiao, setSelectedRegiao] = useState("");
  const [campanha, setCampanha] = useState("");
  const [produto, setProduto] = useState("");
  const [campanhaOptions, setCampanhaOptions] = useState([]);
  const [produtoOptions, setProdutoOptions] = useState([]);
  const [motivoNaoVenda, setMotivoNaoVenda] = useState("");
  const [motivoNaoVendaOptions, setMotivoNaoVendaOptions] = useState([]);
  const [motivoFinalizacao, setMotivoFinalizacao] = useState("");
  const [motivoFinalizacaoOptions, setMotivoFinalizacaoOptions] = useState([]);
  const [closingSummary, setClosingSummary] = useState([]);
  const [statusSummary, setStatusSummary] = useState({ total: 0 });
  const [showClosingSummary, setShowClosingSummary] = useState(
    defaultClosingSummaryPrefs.visible
  );
  const [closingSummaryLayout, setClosingSummaryLayout] = useState(
    defaultClosingSummaryPrefs.layout
  );
  const [lastTicketOnly, setLastTicketOnly] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [supportTimeFilter, setSupportTimeFilter] = useState(null);

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (user?.id != null) {
      setOptionalColumnVisible(loadOptionalColumnVisibility(user.id));
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id != null) {
      const p = loadClosingSummaryPrefs(user.id);
      setShowClosingSummary(p.visible);
      setClosingSummaryLayout(p.layout);
    }
  }, [user?.id]);

  const handleOptionalColumnsChange = (event) => {
    const raw = event.target.value;
    const nextIds =
      typeof raw === "string"
        ? raw.split(",").filter(Boolean)
        : raw.filter(Boolean);
    const vis = {};
    REPORT_OPTIONAL_COLUMN_ORDER.forEach((id) => {
      vis[id] = nextIds.includes(id);
    });
    setOptionalColumnVisible(vis);
    if (user?.id != null) saveOptionalColumnVisibility(user.id, vis);
  };

  useEffect(() => {
    let isMounted = true;

    const defaultSituacaoOptions = ["ENGAJADO", "NEUTRO", "ESPECULANDO"];
    const defaultCanalOptions = [
      "1 Rls ORG",
      "2 Rls PAT",
      "3 CRI CAMP",
      "4 CRI TURB",
      "5 YouTube",
      "6 TikTok",
      "7 Linkedin",
      "8 Site",
      "9 E-commerce",
      "10 LP",
      "11 IND CLI",
      "12 DISP WTS",
      "13 ML",
      "14 IND TEC",
      "15 IND FOR",
      "16 E-mail MKT",
      "17 OUT CONS",
      "18 SDRS",
      "19 REPRE",
    ];
    const defaultRegiaoOptions = [
      "NORTE",
      "NORDESTE",
      "CENTRO OESTE",
      "SUL",
      "SUDESTE",
      "SP",
    ];

    const loadKanbanLanes = async () => {
      try {
        const { data } = await api.get("/tag/kanban");
        if (isMounted) {
          setKanbanLanes(
            Array.isArray(data) ? data : data?.lista || data?.tags || []
          );
        }
      } catch (err) {
        if (isMounted) setKanbanLanes([]);
      }
    };

    const loadMotivoNaoVendaOptions = async () => {
      try {
        const { data } = await api.get("/ticketreport/motivos-nao-venda");
        if (isMounted) {
          setMotivoNaoVendaOptions(Array.isArray(data?.motivos) ? data.motivos : []);
        }
      } catch (err) {
        if (isMounted) setMotivoNaoVendaOptions([]);
      }
    };

    const loadMotivoFinalizacaoOptions = async () => {
      try {
        const { data } = await api.get("/ticketreport/motivos-finalizacao");
        if (isMounted) {
          setMotivoFinalizacaoOptions(
            Array.isArray(data?.motivos) ? data.motivos : []
          );
        }
      } catch (err) {
        if (isMounted) setMotivoFinalizacaoOptions([]);
      }
    };

    const loadCampanhaProdutoOptions = async () => {
      try {
        const [campanhasRes, produtosRes] = await Promise.all([
          api.get("/ticketreport/campanhas"),
          api.get("/ticketreport/produtos"),
        ]);
        if (!isMounted) return;
        setCampanhaOptions(
          Array.isArray(campanhasRes?.data?.options) ? campanhasRes.data.options : []
        );
        setProdutoOptions(
          Array.isArray(produtosRes?.data?.options) ? produtosRes.data.options : []
        );
      } catch (err) {
        if (isMounted) {
          setCampanhaOptions([]);
          setProdutoOptions([]);
        }
      }
    };

    const loadCustomAttributes = async () => {
      if (!user?.companyId) {
        if (isMounted) {
          setSituacaoOptions(defaultSituacaoOptions);
          setCanalOptions(defaultCanalOptions);
          setRegiaoOptions(defaultRegiaoOptions);
        }
        return;
      }

      setLoadingCustomAttributes(true);
      try {
        const [situacaoResponse, canalResponse, regiaoResponse] =
          await Promise.all([
            api.get("/custom-attributes", {
              params: { context: "CONTACT", param: "SITUACAO" },
            }),
            api.get("/custom-attributes", {
              params: { context: "CONTACT", param: "CANAL" },
            }),
            api.get("/custom-attributes", {
              params: { context: "CONTACT", param: "REGIAO" },
            }),
          ]);

        if (!isMounted) return;

        setSituacaoOptions(
          situacaoResponse?.data?.length
            ? situacaoResponse.data
            : defaultSituacaoOptions
        );
        setCanalOptions(
          canalResponse?.data?.length ? canalResponse.data : defaultCanalOptions
        );
        setRegiaoOptions(
          regiaoResponse?.data?.length
            ? regiaoResponse.data
            : defaultRegiaoOptions
        );
      } catch (err) {
        if (isMounted) {
          setSituacaoOptions(defaultSituacaoOptions);
          setCanalOptions(defaultCanalOptions);
          setRegiaoOptions(defaultRegiaoOptions);
        }
      } finally {
        if (isMounted) setLoadingCustomAttributes(false);
      }
    };

    loadKanbanLanes();
    loadCustomAttributes();
    loadMotivoNaoVendaOptions();
    loadMotivoFinalizacaoOptions();
    loadCampanhaProdutoOptions();

    return () => {
      isMounted = false;
    };
  }, [user?.companyId]);

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setTagIds(tags);
  };

  const handleSelectedChannels = (channels) => {
    setSelectedChannels(channels);
  };

  const handleSupportTimeFilter = (filter) => {
    setSupportTimeFilter(filter);
  };

  const buildReportParams = (page, pageSize) => {
    const supportRange = parseSupportTimeRange(supportTimeFilter);
    return {
      searchParam,
      contactId: selectedContactId,
      whatsappId: JSON.stringify(selectedWhatsapp),
      tags: JSON.stringify(tagIds),
      users: JSON.stringify(userIds),
      queueIds: JSON.stringify(queueIds),
      status: JSON.stringify(selectedStatus),
      kanbanLane: selectedKanban,
      situacao: selectedSituacao,
      canal: selectedCanal,
      regiao: selectedRegiao,
      campanha,
      produto,
      motivoNaoVenda,
      motivoFinalizacao,
      lastTicketOnly: lastTicketOnly ? "true" : "false",
      channels: JSON.stringify(selectedChannels),
      supportTimeMin: supportRange.min,
      supportTimeMax: supportRange.max,
      dateFrom,
      dateTo,
      page,
      pageSize,
      onlyRated: onlyRated ? "true" : "false",
    };
  };

  const exportarGridParaExcel = async () => {
    setLoading(true); // Define o estado de loading como true durante o carregamento

    try {
      const data = await getReport(buildReportParams(1, 9999999));

      const ticketsData = data.tickets.map((ticket) => {
        const formatDate = (dateString) => {
          if (!dateString || dateString === null) {
            return { date: "", time: "" };
          }

          if (dateString.includes("/") && dateString.includes(" ")) {
            const [datePart, timePart] = dateString.split(" ");
            return {
              date: datePart,
              time: timePart,
            };
          }

          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
            return { date: "", time: "" };
          }

          return {
            date: date.toLocaleDateString("pt-BR"),
            time: date.toLocaleTimeString("pt-BR"),
          };
        };

        const formatSupportTime = (timeString) => {
          if (!timeString) return "";

          if (timeString === "0") return "0min";

          const match = timeString.match(
            /(\d+)\s*d,?\s*(\d+)\s*hr?s?\s*e?\s*(\d+)\s*m/
          );
          if (!match) return timeString;

          const days = parseInt(match[1]);
          const hours = parseInt(match[2]);
          const minutes = parseInt(match[3]);

          let result = "";
          if (days > 0) result += `${days}d`;
          if (hours > 0) result += `${hours}h`;
          if (minutes > 0) result += `${minutes}min`;

          return result || "0min";
        };

        const createdAtFormatted = formatDate(ticket.createdAt);
        const closedAtFormatted = formatDate(ticket.closedAt);

        return {
          id: ticket.id,
          Conexão: ticket.whatsappName,
          Contato: ticket.contactName,
          WhatsApp: ticket.contactNumber || "-",
          Usuário: ticket.userName,
          Fila: ticket.queueName,
          Kanban: ticketKanbanLabel(ticket),
          Status: ticket.status,
          DataAbertura: createdAtFormatted.date,
          HoraAbertura: createdAtFormatted.time,
          DataFechamento: closedAtFormatted.date,
          HoraFechamento: closedAtFormatted.time,
          TempoDeAtendimento: formatSupportTime(ticket.supportTime),
          nps: ticket.NPS || "",
          ValorDaVenda:
            ticket.valorVenda !== undefined && ticket.valorVenda !== null
              ? ticket.valorVenda
              : "-",
          MotivoDaNaoVenda: ticket.motivoNaoVenda || "-",
          MotivoFinalizacao: ticket.motivoFinalizacao || "-",
          FinalizadoComVenda:
            ticket.finalizadoComVenda === true
              ? "Sim"
              : ticket.finalizadoComVenda === false
              ? "Não"
              : "-",
          Situação: ticket.situacao || "-",
          Canal: ticket.canal || "-",
          Região: ticket.regiao || "-",
          Campanha: ticket.campanha || "-",
          Produto: ticket.produto || "-",
        };
      });

      console.log(ticketsData);

      // RDS.CHAT - preparando os dados aqui...
      const getTranslation = (key, fallback) => {
        const translation = i18n.t(key);
        return translation !== key ? translation : fallback;
      };

      const formatDateBR = (dateStr) => {
        if (!dateStr)
          return getTranslation(
            "reports.exportExcel.notInformed",
            "Não informado"
          );
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      };

      const translations = {
        title: getTranslation(
          "reports.exportExcel.title",
          "Relatórios de Atendimentos"
        ),
        startDate: getTranslation(
          "reports.exportExcel.startDate",
          "Data inicial"
        ),
        endDate: getTranslation("reports.exportExcel.endDate", "Data final"),
        columns: {
          id: getTranslation("reports.exportExcel.columns.id", "ID"),
          connection: getTranslation(
            "reports.exportExcel.columns.connection",
            "Conexão"
          ),
          contact: getTranslation(
            "reports.exportExcel.columns.contact",
            "Contato"
          ),
          user: getTranslation("reports.exportExcel.columns.user", "Usuário"),
          queue: getTranslation("reports.exportExcel.columns.queue", "Fila"),
          kanban: getTranslation("reports.exportExcel.columns.kanban", "Kanban"),
          status: getTranslation(
            "reports.exportExcel.columns.status",
            "Status"
          ),
          openDate: getTranslation(
            "reports.exportExcel.columns.openDate",
            "Data Abertura"
          ),
          openTime: getTranslation(
            "reports.exportExcel.columns.openTime",
            "Hora Abertura"
          ),
          closeDate: getTranslation(
            "reports.exportExcel.columns.closeDate",
            "Data Fechamento"
          ),
          closeTime: getTranslation(
            "reports.exportExcel.columns.closeTime",
            "Hora Fechamento"
          ),
          supportTime: getTranslation(
            "reports.exportExcel.columns.supportTime",
            "Tempo de Atendimento"
          ),
          nps: getTranslation("reports.exportExcel.columns.nps", "NPS"),
          valorVenda: getTranslation(
            "reports.exportExcel.columns.valorVenda",
            "Valor da Venda"
          ),
          motivoNaoVenda: getTranslation(
            "reports.exportExcel.columns.motivoNaoVenda",
            "Motivo da Não Venda"
          ),
          motivoFinalizacao: getTranslation(
            "reports.exportExcel.columns.motivoFinalizacao",
            "Motivo da finalização"
          ),
          finalizadoComVenda: getTranslation(
            "reports.exportExcel.columns.finalizadoComVenda",
            "Finalizado com Venda"
          ),
          situacao: getTranslation(
            "reports.exportExcel.columns.situacao",
            "Situação"
          ),
          canal: getTranslation(
            "reports.exportExcel.columns.canal",
            "Canal"
          ),
          regiao: getTranslation(
            "reports.exportExcel.columns.regiao",
            "Região"
          ),
          campanha: getTranslation(
            "reports.exportExcel.columns.campanha",
            "Campanha"
          ),
          produto: getTranslation(
            "reports.exportExcel.columns.produto",
            "Produto"
          ),
        },
      };

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        [translations.title],
        [`${translations.startDate}: ${formatDateBR(dateFrom)}`],
        [`${translations.endDate}: ${formatDateBR(dateTo)}`],
        [],
        [
          translations.columns.id,
          translations.columns.connection,
          translations.columns.contact,
          "WhatsApp",
          translations.columns.user,
          translations.columns.queue,
          translations.columns.kanban,
          translations.columns.status,
          translations.columns.openDate,
          translations.columns.openTime,
          translations.columns.closeDate,
          translations.columns.closeTime,
          translations.columns.supportTime,
          translations.columns.nps,
          translations.columns.valorVenda,
          translations.columns.motivoNaoVenda,
          translations.columns.motivoFinalizacao,
          translations.columns.finalizadoComVenda,
          translations.columns.situacao,
          translations.columns.canal,
          translations.columns.regiao,
          translations.columns.campanha,
          translations.columns.produto,
        ],
      ]);

      XLSX.utils.sheet_add_json(ws, ticketsData, {
        origin: "A6",
        skipHeader: true,
      });

      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { c: 0, r: 0 }, e: { c: 22, r: 0 } });

      if (!ws["!cols"]) ws["!cols"] = [];
      ws["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 8 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
        { wch: 15 },
      ];

      if (!ws["!rows"]) ws["!rows"] = [];
      ws["!rows"] = [
        { hpt: 25 },
        { hpt: 20 },
        { hpt: 20 },
        { hpt: 10 },
        { hpt: 25 },
      ];

      const range = XLSX.utils.decode_range(ws["!ref"]);

      for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
        for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          if (!ws[cellAddress]) continue;

          if (!ws[cellAddress].s) ws[cellAddress].s = {};

          if (rowNum === 0) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          } else if (rowNum === 1 || rowNum === 2) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 12 },
              fill: { fgColor: { rgb: "E7E6E6" } },
            };
          } else if (rowNum === 4) {
            ws[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "70AD47" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendimentos");
      XLSX.writeFile(wb, "relatorio-de-atendimentos.xlsx", {
        cellStyles: true,
        bookType: "xlsx",
      });

      setPageNumber(pageNumber);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (pageNumber, overridePageSize) => {
    setLoading(true); // Define o estado de loading como true durante o carregamento
    console.log(onlyRated);
    const pageSizeForRequest =
      overridePageSize !== undefined && overridePageSize !== null
        ? overridePageSize
        : resolvedPageSize;
    try {
      const data = await getReport({
        ...buildReportParams(pageNumber, pageSizeForRequest),
        includeClosingSummary: "true",
      });

      setTotalTickets(data.totalTickets.total);

      // Verifica se há mais resultados para definir hasMore
      setHasMore(data.tickets.length === pageSizeForRequest);

      setTickets(data.tickets); // Se for a primeira página, substitua os tickets
      setClosingSummary(
        Array.isArray(data.closingSummary) ? data.closingSummary : []
      );
      setStatusSummary(
        data.statusSummary && typeof data.statusSummary === "object"
          ? data.statusSummary
          : { total: data.totalTickets?.total || 0 }
      );

      setPageNumber(pageNumber); // Atualiza o estado da página atual
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false); // Define o estado de loading como false após o carregamento
    }
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setUserIds(users);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    setSelectedWhatsapp(whatsapp);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);

    setSelectedStatus(statusFilter);
  };
  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return (
          <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />
        );
      case "instagram":
        return (
          <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />
        );
      case "whatsapp":
        return (
          <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
        );
      default:
        return "error";
    }
  };

  const renderOption = (option) => {
    if (option.number) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: "10px",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name} - {option.number}
          </Typography>
        </>
      );
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const handleSelectOption = (e, newValue) => {
    setSelectedContactId(newValue.id);
    setSearchParam("");
  };

  const renderOptionLabel = (option) => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };
  const filter = createFilterOptions({
    trim: true,
  });

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };
  const renderContactAutocomplete = () => {
    return (
      <Autocomplete
        fullWidth
        options={options}
        loading={loading}
        clearOnBlur
        autoHighlight
        freeSolo
        size="small"
        clearOnEscape
        getOptionLabel={renderOptionLabel}
        renderOption={renderOption}
        filterOptions={createAddContactOption}
        onChange={(e, newValue) => handleSelectOption(e, newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={i18n.t("newTicketModal.fieldLabel")}
            variant="outlined"
            autoFocus
            size="small"
            onChange={(e) => setSearchParam(e.target.value)}
            // onKeyPress={(e, newValue) => handleSelectOption(e, newValue)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  };

  return (
    <MainContainer className={classes.mainContainer}>
      {openTicketMessageDialog && (
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen.id}
        />
      )}
      <div className={classes.titleRow}>
        <Title>{i18n.t("reports.title")}</Title>
        <div className={classes.reportColumnsToolbarBlock}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.reportColumnsSelect}
          >
            <InputLabel shrink id="report-table-columns-select-label">
              {i18n.t("reports.columnPicker.button")}
            </InputLabel>
            <Select
              labelId="report-table-columns-select-label"
              id="report-table-columns-select"
              multiple
              value={selectedOptionalColumnIds}
              onChange={handleOptionalColumnsChange}
              input={
                <OutlinedInput
                  notched
                  label={i18n.t("reports.columnPicker.button")}
                />
              }
              renderValue={(selected) => {
                if (!selected.length) {
                  return i18n.t("reports.columnPicker.emptySelection");
                }
                if (selected.length <= 2) {
                  return selected
                    .map((id) => reportOptionalColumnLabel(id))
                    .join(", ");
                }
                return i18n.t("reports.columnPicker.selectedSummary", {
                  count: selected.length,
                });
              }}
              MenuProps={{
                PaperProps: { style: { maxHeight: 360 } },
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              }}
            >
              {REPORT_OPTIONAL_COLUMN_ORDER.map((colId) => (
                <MenuItem key={colId} value={colId}>
                  <Checkbox
                    checked={selectedOptionalColumnIds.indexOf(colId) > -1}
                    color="primary"
                    size="small"
                  />
                  <ListItemText primary={reportOptionalColumnLabel(colId)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={showClosingSummary}
                onChange={(e) => {
                  const v = e.target.checked;
                  setShowClosingSummary(v);
                  if (user?.id != null) {
                    saveClosingSummaryPrefs(user.id, {
                      visible: v,
                      layout: closingSummaryLayout,
                    });
                  }
                }}
                size="small"
              />
            }
            label={i18n.t("reports.closingSummaryShowToggle")}
          />
          {showClosingSummary && (
            <ToggleButtonGroup
              size="small"
              value={closingSummaryLayout}
              exclusive
              onChange={(e, val) => {
                if (val == null) return;
                setClosingSummaryLayout(val);
                if (user?.id != null) {
                  saveClosingSummaryPrefs(user.id, {
                    visible: showClosingSummary,
                    layout: val,
                  });
                }
              }}
            >
              <ToggleButton value="table">
                {i18n.t("reports.closingSummaryLayoutList")}
              </ToggleButton>
              <ToggleButton value="cards">
                {i18n.t("reports.closingSummaryLayoutCards")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={showFilters}
                onChange={() => setShowFilters((v) => !v)}
                size="small"
              />
            }
            label={i18n.t("reports.filtersToggle")}
          />
        </div>
      </div>

      <MainHeader
        className={classes.mainHeaderFilter}
        style={{ display: "flex" }}
      >
        <Collapse in={showFilters} style={{ width: "100%" }}>
        <Paper className={classes.mainPaperFilter}>
          <Grid container spacing={2}>
            {/* Primeira linha de filtros - 4 campos */}
            <Grid item xs={12} sm={6} md={3}>
              {renderContactAutocomplete()}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ChannelFilter onFiltered={handleSelectedChannels} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatusFilter onFiltered={handleSelectedStatus} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsersFilter onFiltered={handleSelectedUsers} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SupportTimeFilter onFiltered={handleSupportTimeFilter} />
            </Grid>

            {/* Segunda linha - Filas, datas, switch e botões */}
            <Grid item xs={12} sm={6} md={2}>
              <div style={{ display: "flex", alignItems: "center", marginTop: "-12px" }}>
                <div style={{ width: "100%" }}>
                  <QueueSelectCustom
                    selectedQueueIds={queueIds}
                    onChange={(values) => setQueueIds(values)}
                  />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <div style={{ display: "flex", alignItems: "center", marginTop: "0px" }}>
                <div style={{ width: "100%" }}>
                  <TagsFilter onFiltered={handleSelectedTags} />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={i18n.t("reports.startDate")}
                type="date"
                value={dateFrom}
                variant="outlined"
                fullWidth
                size="small"
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={i18n.t("reports.endDate")}
                type="date"
                value={dateTo}
                variant="outlined"
                fullWidth
                size="small"
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "56px",
                  marginTop: "-7px" 
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={onlyRated}
                      onChange={() => setOnlyRated(!onlyRated)}
                      size="small"
                    />
                  }
                  label={i18n.t("reports.buttons.onlyRated")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={lastTicketOnly}
                      onChange={() => setLastTicketOnly(!lastTicketOnly)}
                      size="small"
                    />
                  }
                  label="Último Ticket"
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
                <IconButton
                  onClick={exportarGridParaExcel}
                  aria-label="Exportar para Excel"
                  size="small"
                  color="primary"
                >
                  <SaveAlt />
                </IconButton>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleFilter(pageNumber)}
                  size="small"
                >
                  {i18n.t("reports.buttons.filter")}
                </Button>
              </div>
            </Grid>

            {/* Terceira linha - filtros adicionais */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl margin="dense" variant="outlined" fullWidth>
                <InputLabel>Kanban</InputLabel>
                <Select
                  value={selectedKanban}
                  onChange={(e) => setSelectedKanban(e.target.value)}
                  label="Kanban"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="open">{i18n.t("tagsKanban.laneDefault")}</MenuItem>
                  {kanbanLanes.map((lane) => (
                    <MenuItem key={lane.id} value={String(lane.id)}>
                      {lane.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl
                margin="dense"
                variant="outlined"
                fullWidth
                disabled={loadingCustomAttributes}
              >
                <InputLabel>Situação</InputLabel>
                <Select
                  value={selectedSituacao}
                  onChange={(e) => setSelectedSituacao(e.target.value)}
                  label="Situação"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {situacaoOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl
                margin="dense"
                variant="outlined"
                fullWidth
                disabled={loadingCustomAttributes}
              >
                <InputLabel>Canal</InputLabel>
                <Select
                  value={selectedCanal}
                  onChange={(e) => setSelectedCanal(e.target.value)}
                  label="Canal"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {canalOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl
                margin="dense"
                variant="outlined"
                fullWidth
                disabled={loadingCustomAttributes}
              >
                <InputLabel>Região</InputLabel>
                <Select
                  value={selectedRegiao}
                  onChange={(e) => setSelectedRegiao(e.target.value)}
                  label="Região"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {regiaoOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl
                margin="dense"
                variant="outlined"
                fullWidth
              >
                <InputLabel>Campanha</InputLabel>
                <Select
                  value={campanha}
                  onChange={(e) => setCampanha(e.target.value)}
                  label="Campanha"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {campanhaOptions.map((option, index) => (
                    <MenuItem key={`campanha-${index}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl
                margin="dense"
                variant="outlined"
                fullWidth
              >
                <InputLabel>Produto</InputLabel>
                <Select
                  value={produto}
                  onChange={(e) => setProduto(e.target.value)}
                  label="Produto"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {produtoOptions.map((option, index) => (
                    <MenuItem key={`produto-${index}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                freeSolo
                options={motivoNaoVendaOptions}
                value={motivoNaoVenda}
                onChange={(event, newValue) => {
                  setMotivoNaoVenda(typeof newValue === "string" ? newValue : "");
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Evita limpar o valor ao perder foco (blur/reset),
                  // garantindo que o texto digitado seja aplicado no filtro.
                  if (reason === "input" || reason === "clear") {
                    setMotivoNaoVenda(newInputValue || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("reports.filters.motivoNaoVenda")}
                    variant="outlined"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                freeSolo
                options={motivoFinalizacaoOptions}
                value={motivoFinalizacao}
                onChange={(event, newValue) => {
                  setMotivoFinalizacao(
                    typeof newValue === "string" ? newValue : ""
                  );
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Mesmo comportamento do campo de motivo de não venda:
                  // não perder texto no blur/reset.
                  if (reason === "input" || reason === "clear") {
                    setMotivoFinalizacao(newInputValue || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={i18n.t("reports.filters.motivoFinalizacao")}
                    variant="outlined"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid>

          </Grid>
        </Paper>
        </Collapse>
      </MainHeader>
      {showClosingSummary && closingSummary.length > 0 && (
        <Paper style={{ marginBottom: 16, padding: 16 }} variant="outlined">
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("reports.closingSummaryTitle")}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
            {i18n.t("reports.statusSummaryHint")}
          </Typography>
          {closingSummaryLayout === "table" ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("reports.table.user")}</TableCell>
                  <TableCell>
                    {i18n.t("reports.table.motivoFinalizacao")}
                  </TableCell>
                  <TableCell align="right">
                    {i18n.t("reports.closingSummaryCount")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {closingSummary.map((row, idx) => (
                  <TableRow key={`${row.userName}-${row.motivoFinalizacao}-${idx}`}>
                    <TableCell>{row.userName || "-"}</TableCell>
                    <TableCell>
                      {row.motivoFinalizacao === "-"
                        ? i18n.t("reports.closingSummaryNoReason")
                        : row.motivoFinalizacao}
                    </TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className={classes.closingSummaryCardsWrap}>
              {closingSummary.map((row, idx) => {
                const reasonLabel =
                  row.motivoFinalizacao === "-"
                    ? i18n.t("reports.closingSummaryNoReason")
                    : row.motivoFinalizacao;
                return (
                  <div
                    key={`${row.userName}-${row.motivoFinalizacao}-${idx}`}
                    className={classes.closingSummaryCard}
                  >
                    <div className={classes.closingSummaryCardCount}>{row.total}</div>
                    <div className={classes.closingSummaryCardMeta}>
                      <Typography
                        component="div"
                        variant="caption"
                        color="textPrimary"
                        style={{ fontWeight: 600 }}
                      >
                        {row.userName || "-"}
                      </Typography>
                      <Typography component="div" variant="caption" color="textSecondary">
                        {reasonLabel}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Paper>
      )}
      <Paper className={classes.mainPaperTable} variant="outlined">
        <Table size="small" id="grid-attendants" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead>
            <TableRow className={classes.tableHeadStickyRow}>
              <TableCell align="center" className={classes.tableStickyId}>
                {i18n.t("reports.table.id")}
              </TableCell>
              {REPORT_OPTIONAL_COLUMN_ORDER.map((colId) =>
                optionalColumnVisible[colId] ? (
                  <TableCell key={colId} align={optionalColumnAlign(colId)}>
                    {reportOptionalColumnLabel(colId)}
                  </TableCell>
                ) : null
              )}
              <TableCell align="center" className={classes.tableStickyActions}>
                {i18n.t("reports.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell align="center" className={classes.tableStickyId}>
                    {ticket.id}
                  </TableCell>
                  {REPORT_OPTIONAL_COLUMN_ORDER.map((colId) =>
                    optionalColumnVisible[colId] ? (
                      <TableCell key={colId} align={optionalColumnAlign(colId)}>
                        {renderOptionalTicketCellValue(ticket, colId)}
                      </TableCell>
                    ) : null
                  )}
                  <TableCell align="center" className={classes.tableStickyActions}>
                    <Typography
                      noWrap
                      component="span"
                      variant="body2"
                      color="textPrimary"
                    >
                      <Tooltip title="Logs do Ticket">
                        <History
                          onClick={() => {
                            setOpenTicketMessageDialog(true);
                            setTicketOpen(ticket);
                          }}
                          fontSize="small"
                          style={{
                            color: blue[700],
                            cursor: "pointer",
                            marginLeft: 10,
                            verticalAlign: "middle",
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Acessar Ticket">
                        <Forward
                          onClick={() => {
                            history.push(`/tickets/${ticket.uuid}`);
                          }}
                          fontSize="small"
                          style={{
                            color: green[700],
                            cursor: "pointer",
                            marginLeft: 10,
                            verticalAlign: "middle",
                          }}
                        />
                      </Tooltip>
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </>
          </TableBody>
        </Table>
      </Paper>

      <div>
        <Grid container>
          <Grid item xs={12} sm={8} md={8}>
            <Pagination
              count={Math.max(
                1,
                Math.ceil(totalTickets / resolvedPageSize)
              )}
              page={pageNumber} // Define a página atual
              onChange={(event, value) => handleFilter(value)} // Função de callback para mudanças de página
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Typography variant="body2" color="textSecondary">
                {`${i18n.t("reports.recordsShownLabel")}: ${tickets.length}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${i18n.t("reports.recordsTotalLabel")}: ${totalTickets || 0}`}
              </Typography>
              {(statusSummary?.ABERTO > 0 || statusSummary?.FECHADO > 0) && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    {`${i18n.t("reports.statusSummaryOpen")}: ${statusSummary.ABERTO || 0}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {`${i18n.t("reports.statusSummaryClosed")}: ${statusSummary.FECHADO || 0}`}
                  </Typography>
                </>
              )}
              <FormControl margin="dense" variant="outlined" style={{ minWidth: 140 }}>
                <InputLabel>{i18n.t("tickets.search.ticketsPerPage")}</InputLabel>
                <Select
                  labelId="dialog-select-prompt-label"
                  id="dialog-select-prompt"
                  name="pageSize"
                  value={pageSizeSelect}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPageSizeSelect(v);
                    setPageNumber(1);
                    let nextSize = 10;
                    if (v === "custom") {
                      const n = parseInt(
                        String(customPageSize).replace(/\D/g, ""),
                        10
                      );
                      nextSize = !n || n < 1 ? 10 : Math.min(999, n);
                    } else {
                      nextSize = parseInt(v, 10) || 10;
                    }
                    handleFilter(1, nextSize);
                  }}
                  label={i18n.t("tickets.search.ticketsPerPage")}
                  fullWidth
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  <MenuItem value="5">5</MenuItem>
                  <MenuItem value="10">10</MenuItem>
                  <MenuItem value="20">20</MenuItem>
                  <MenuItem value="50">50</MenuItem>
                  <MenuItem value="100">100</MenuItem>
                  <MenuItem value="custom">
                    {i18n.t("reports.customPageSizeOption")}
                  </MenuItem>
                </Select>
              </FormControl>
              {pageSizeSelect === "custom" && (
                <TextField
                  label={i18n.t("reports.customPageSizeLabel")}
                  type="number"
                  size="small"
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 1, max: 999 }}
                  value={customPageSize}
                  onChange={(e) => setCustomPageSize(e.target.value)}
                  onBlur={() => {
                    const n = parseInt(
                      String(customPageSize).replace(/\D/g, ""),
                      10
                    );
                    const nextSize =
                      !n || n < 1 ? 10 : Math.min(999, n);
                    setPageNumber(1);
                    handleFilter(1, nextSize);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const n = parseInt(
                        String(customPageSize).replace(/\D/g, ""),
                        10
                      );
                      const nextSize =
                        !n || n < 1 ? 10 : Math.min(999, n);
                      setPageNumber(1);
                      handleFilter(1, nextSize);
                    }
                  }}
                  style={{ width: 120 }}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default Reports;
